'use client';
import type {
  AIChatConfig,
  DealerWeb2TextStatus,
  LocationWeb2TextStatus,
  Web2TextConfig,
} from '@dealerportal/common';
import { logger } from '@dealerportal/core';
import ky from 'ky';
import {
  Suspense,
  type Usable,
  use,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Chat from './lib';

interface ChatWidgetLoaderProps {
  dealerStatus: Usable<{
    dealerStatus: DealerWeb2TextStatus | null;
  }>;
  clientId: number;
  universalRetailerId: string;
  web2TextConfiguration: Web2TextConfig;
  recaptcha:
    | {
        Enabled: boolean;
        SiteKey?: string;
      }
    | undefined;
  aiSettings: AIChatConfig;
  fallbackStorePhone: string;
}

const forceShowWeb2TextWidget =
  process.env['NEXT_PUBLIC_FORCE_WEB2TEXT_WIDGET'] === 'true';

const locationHasAnyValidChannel = (
  location: LocationWeb2TextStatus
): boolean =>
  location.MessagingChannels?.sms?.Status === 'VALID' ||
  location.MessagingChannels?.whatsapp?.Status === 'VALID';

export async function GetWeb2TextStatus(
  origin: string,
  universalRetailerId: string
): Promise<{
  dealerStatus: DealerWeb2TextStatus | null;
}> {
  try {
    const web2TextResponse = await ky
      // No `cache` override: the status route sends `Cache-Control: private, max-age=…`,
      // so the browser HTTP cache absorbs the repeat requests caused by this loader
      // remounting on client-side navigation.
      .get(`${origin}/api/web2Text/status`, {
        searchParams: {
          UniversalRetailerId: universalRetailerId,
        },
      })
      .json<{
        Success: boolean;
        DealerStatus: DealerWeb2TextStatus;
      }>();
    // Enable Web2Text if the endpoint is reachable, the dealer is valid, and at least one
    // location has at least one valid messaging channel (SMS or WhatsApp).
    if (
      web2TextResponse.Success &&
      web2TextResponse.DealerStatus.Status === 'VALID' &&
      web2TextResponse.DealerStatus.Locations.some(locationHasAnyValidChannel)
    ) {
      return {
        dealerStatus: web2TextResponse.DealerStatus,
      };
    }
  } catch (e) {
    logger.warn("[WEB2TEXT] Couldn't reach Web2Text status API!");
    logger.withError(e).warn('[WEB2TEXT] Error details:');
  }
  return {
    dealerStatus: null,
  };
}

const ChatWidgetLoaderInternal = (props: ChatWidgetLoaderProps) => {
  const statusContext = use(props.dealerStatus);
  const dealerStatus = statusContext.dealerStatus;
  const effectiveWeb2TextConfiguration = forceShowWeb2TextWidget
    ? {
        ...props.web2TextConfiguration,
        EnableWeb2Text: true,
        ChatColor: props.web2TextConfiguration.ChatColor ?? 'Default',
        ChatPosition: props.web2TextConfiguration.ChatPosition ?? 'right',
        ChatCallToActionLabel:
          props.web2TextConfiguration.ChatCallToActionLabel ?? 'Chat',
        ChatPromptMessage:
          props.web2TextConfiguration.ChatPromptMessage ??
          'How can we help you?',
      }
    : { ...props.web2TextConfiguration };
  const showWeb2Text =
    (forceShowWeb2TextWidget || dealerStatus != null) &&
    effectiveWeb2TextConfiguration.EnableWeb2Text;
  const showAi = props.aiSettings.EnableAIChat;
  return (
    <>
      {(showWeb2Text || showAi) && (
        <Chat
          DealerStatus={dealerStatus ?? undefined}
          AIChatConfig={props.aiSettings}
          W2TConfig={{
            ...effectiveWeb2TextConfiguration,
            EnableWeb2Text: showWeb2Text,
          }}
          Recaptcha={props.recaptcha ?? { Enabled: false }}
          FallbackStorePhone={props.fallbackStorePhone}
        />
      )}
    </>
  );
};
export const ChatWidgetLoader = (
  props: Omit<ChatWidgetLoaderProps, 'dealerStatus'>
) => {
  const [origin, setOrigin] = useState<string | null>(null);
  // Prevent SSR from running before the origin is available
  useEffect(() => {
    setOrigin(document?.location?.origin ?? null);
  }, []);
  const web2TextStatus = useMemo(
    () =>
      origin != null &&
      (props.web2TextConfiguration.EnableWeb2Text || forceShowWeb2TextWidget)
        ? GetWeb2TextStatus(origin, props.universalRetailerId)
        : Promise.resolve({ dealerStatus: null }),
    [
      origin,
      props.universalRetailerId,
      props.web2TextConfiguration.EnableWeb2Text,
    ]
  );
  return (
    <Suspense fallback={null}>
      <ChatWidgetLoaderInternal {...props} dealerStatus={web2TextStatus} />
    </Suspense>
  );
};
